import { FC } from 'react';
import clsx from 'clsx';
import CloseThinIcon from '~/icons/close-thin.svg';
import { useTranslation } from '~/shared/utils/translation';
import { useStats } from '~/features/productList/hooks/useStats';
import { ResetButton } from '~/shared/components/Button/ResetButton';
import { CountBadge } from '~/features/productList/components/CountBadge';
import styles from './FilterSelectionHeader.module.scss';

type FilterSelectionHeaderProps = {
    label: string;
    selectedCount: number;
    onClear: () => void;
    // Override the interpolated {label} per state, for facets where the "all"
    // and "selected" wording use different nouns rather than the same one (e.g. price).
    allLabel?: string;
    selectedLabel?: string;
};

export const FilterSelectionHeader: FC<FilterSelectionHeaderProps> = ({
    label,
    selectedCount,
    onClear,
    allLabel,
    selectedLabel,
}) => {
    const { translate } = useTranslation();
    const { nbHits } = useStats();
    const countLabel = translate('plp.facetSelection.resultCount', {
        count: (nbHits ?? 0).toString(),
    });

    if (!selectedCount) {
        // The design shows the count on two lines (amount, then unit) when there's
        // no selection. The translated string is one CMS-managed phrase (e.g. "578
        // varer"), so we split on its first space rather than restructuring the
        // translation key, which would break the template already set up in Umbraco.
        const unitStartIndex = countLabel.indexOf(' ');
        const countAmount =
            unitStartIndex === -1 ? countLabel : countLabel.slice(0, unitStartIndex);
        const countUnit = unitStartIndex === -1 ? null : countLabel.slice(unitStartIndex + 1);

        return (
            <div className={styles.selectionHeader}>
                <div className={clsx(styles.selectionHeaderRow, styles.selectionHeaderAllRow)}>
                    <span className={styles.selectionHeaderTitle}>
                        {translate('plp.facetSelection.all', { label: allLabel ?? label })}
                    </span>
                    <span
                        className={clsx(
                            styles.selectionHeaderCount,
                            styles.selectionHeaderCountStacked,
                        )}
                    >
                        <span>{countAmount}</span>
                        {countUnit && <span>{countUnit}</span>}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.selectionHeader}>
            <div className={styles.selectionHeaderRow}>
                <span className={styles.selectionHeaderTitle}>
                    {translate('plp.facetSelection.selected', { label: selectedLabel ?? label })}
                    <CountBadge count={selectedCount} />
                </span>
                <ResetButton
                    className={styles.selectionHeaderClearChip}
                    aria-label={translate('plp.facetSelection.clear')}
                    onClick={onClear}
                >
                    {translate('plp.facetSelection.clear')}
                    <CloseThinIcon />
                </ResetButton>
            </div>
            <span className={styles.selectionHeaderCount}>{countLabel}</span>
        </div>
    );
};
